import { and, bool, equal, not, or, reads } from '@ember/object/computed';
import { tracked } from '@glimmer/tracking';
import RequestStepsChannelFieldsStep from '../components/request/steps/channel-fields-step';
import RequestStepsChannelUploadsStep from '../components/request/steps/channel-uploads-step';
import RequestStepsClientTypeStep from '../components/request/steps/client-type-step';
import RequestStepsClinicianStep from '../components/request/steps/clinician-step';
import RequestStepsDateStep from '../components/request/steps/date-step';
import RequestStepsInformationStep from '../components/request/steps/information-step';
import RequestStepsLocationStep from '../components/request/steps/location-step';
import RequestStepsMessageStep from '../components/request/steps/message-step';
import RequestStepsPrescreenerCategoryStep from '../components/request/steps/prescreener-category-step';
import RequestStepsServiceStep from '../components/request/steps/service-step';
import Service, { service } from '@ember/service';
import classic from 'ember-classic-decorator';
import emberO, { computed, set, setProperties } from '@ember/object';

const CONDENSED_GROUPS = {
  APPOINTMENT_INFO: 'appointment-info',
  PRESCREENER_CATEGORIES: 'prescreener-categories',
};

export const STEP_IDS = {
  SELECT_CLINICIAN: 'select clinician',
  SELECT_SERVICE: 'select service',
  SELECT_LOCATION: 'select location',
  SELECT_DATE: 'select date',
  CLIENT_TYPE: 'client type',
  PRESCREENER_REASON_FOR_CARE: 'prescreener reason for care',
  PRESCREENER_BILLING_PAYMENT: 'prescreener billing & payment',
  PRESCREENER_ADMINISTRATIVE: 'prescreener administrative',
  PRESCREENER_CLIENT_PREFERENCES: 'prescreener client preferences',
  INTAKE_QUESTIONS: 'intake questions',
  INTAKE_DOCUMENTS: 'intake documents',
  EXTERNAL_DETAILS: 'external details',
  CLIENT_INFORMATION: 'client information',
  REVIEW_AND_CONFIRM: 'review and confirm',
  CONFIRMATION: 'confirmation',
  REQUEST_SENT: 'request sent',
  REQUEST_CANCELLED: 'request cancelled',
  UNAVAILABLE: 'unavailable',
};

@classic
export default class WidgetStepsService extends Service {
  @service currentPractice;
  @service session;
  @service request;
  @service media;
  @service store;
  @service widgetToken;
  @service prescreener;

  @tracked activeStep;

  clinicianHasMonarchProfession = true;

  @reads('currentPractice.featureEnableClientPortalPrescreenerSettings')
  featureEnableClientPortalPrescreenerSettings;

  @equal('currentPractice.clientTypesAllowedToBookAppt.length', 1)
  onlyOneClientTypeAllowedToBookAppt;

  @reads('currentPractice.featurePrescreenerV2') featurePrescreenerV2;
  @reads('prescreener.hasNavCategories') prescreenerHasCategories;
  @reads('prescreener.questionRecord') prescreenerLoaded;
  @bool('prescreener.preview.isPreview') isPrescreenerPreview;

  @computed('featurePrescreenerV2', 'isPrescreenerPreview', 'prescreenerLoaded', 'isNewClient')
  get hasPrescreenerStep() {
    let { featurePrescreenerV2, isPrescreenerPreview, isNewClient, prescreenerLoaded } = this;
    const isEnabled = isNewClient && prescreenerLoaded;
    const canShow = !!isPrescreenerPreview || isEnabled;
    return featurePrescreenerV2 && canShow;
  }

  @computed('hasPrescreenerStep', 'prescreenerHasCategories')
  get hasPrescreenerStepWithCategories() {
    return this.hasPrescreenerStep && this.prescreenerHasCategories;
  }

  @not('hasPrescreenerStep') hasLegacyPrescreener;
  @and('clinicianHasMonarchProfession', 'session.isClientPortalMonarchOrSpWebsite')
  canShowPrescreenerNav;

  @or('session.isMonarchEmbedded', 'canShowPrescreenerNav', 'hasPrescreenerStep') isCondensed;
  @or('session.addMessage', 'canShowPrescreenerNav') canAddMessageStep;

  @not('session.currentClient') isNewClient;
  @and('canAddMessageStep', 'isNewClient', 'hasLegacyPrescreener') hasMessageStep;
  @and('hasMessageStep', 'currentPractice.showPrescreenerSection', 'hasLegacyPrescreener')
  canDisplayPrescreenerSection;

  @computed('hasMessageStep', 'hasPrescreenerStep', 'hasLegacyPrescreener', 'isPrescreenerPreview')
  get steps() {
    let isPrescreenerPreview = !!this.prescreener.preview.isPreview;
    return [
      emberO.create({
        id: STEP_IDS.SELECT_CLINICIAN,
        isActive: false,
        route: 'clinician',
        title: 'booking.steps.clinician',
        loadingTitle: 'booking.steps.loading.clinicians',
        completedTitle: 'booking.steps.completedTitles.clinician',
        completedKey: 'clinician',
        menuComponent: RequestStepsClinicianStep,
        isDisabledInPreview: isPrescreenerPreview,
        isCompleteInPreview: isPrescreenerPreview,
        condenseKey: CONDENSED_GROUPS.APPOINTMENT_INFO,
      }),
      emberO.create({
        id: STEP_IDS.SELECT_SERVICE,
        isActive: false,
        route: 'service',
        title: 'booking.steps.service',
        completedTitle: 'booking.steps.completedTitles.service',
        loadingTitle: 'booking.steps.loading.services',
        completedKey: 'cptCode',
        menuComponent: RequestStepsServiceStep,
        isDisabledInPreview: isPrescreenerPreview,
        condenseKey: CONDENSED_GROUPS.APPOINTMENT_INFO,
      }),
      emberO.create({
        id: STEP_IDS.SELECT_LOCATION,
        isActive: false,
        route: 'location',
        title: 'booking.steps.location',
        completedTitle: 'booking.steps.completedTitles.location',
        loadingTitle: 'booking.steps.loading.locations',
        completedKey: 'office',
        menuComponent: RequestStepsLocationStep,
        isDisabledInPreview: isPrescreenerPreview,
        condenseKey: CONDENSED_GROUPS.APPOINTMENT_INFO,
      }),
      emberO.create({
        id: STEP_IDS.SELECT_DATE,
        isActive: false,
        route: 'date',
        title: 'booking.steps.date',
        completedTitle: 'booking.steps.completedTitles.dateTime',
        loadingTitle: 'booking.steps.loading.availability',
        completedKey: 'isReserved',
        menuComponent: RequestStepsDateStep,
        isDisabledInPreview: isPrescreenerPreview,
        condenseKey: CONDENSED_GROUPS.APPOINTMENT_INFO,
      }),
      ...(this.hasLegacyPrescreener
        ? [
            emberO.create({
              id: STEP_IDS.CLIENT_TYPE,
              isActive: false,
              route: 'client-type',
              title: 'booking.steps.clientType',
              completedTitle: 'booking.steps.completedTitles.client',
              completedKey: 'clientType',
              menuComponent: RequestStepsClientTypeStep,
              condenseKey: CONDENSED_GROUPS.APPOINTMENT_INFO,
              forNewClientOnly: true,
            }),
          ]
        : []),
      ...(this.hasPrescreenerStepWithCategories
        ? [
            emberO.create({
              id: STEP_IDS.PRESCREENER_REASON_FOR_CARE,
              route: 'prescreener-reason-for-care',
              title: 'booking.prescreener.categories.reasonForCare',
              completedTitle: 'booking.prescreener.categories.reasonForCare',
              isActive: false,
              condenseKey: CONDENSED_GROUPS.PRESCREENER_CATEGORIES,
              menuComponent: RequestStepsPrescreenerCategoryStep,
              forNewClientOnly: true,
            }),
            emberO.create({
              id: STEP_IDS.PRESCREENER_ADMINISTRATIVE,
              route: 'prescreener-administrative',
              title: 'booking.prescreener.categories.administrative',
              completedTitle: 'booking.prescreener.categories.administrative',
              isActive: false,
              condenseKey: CONDENSED_GROUPS.PRESCREENER_CATEGORIES,
              menuComponent: RequestStepsPrescreenerCategoryStep,
              forNewClientOnly: true,
            }),
            emberO.create({
              id: STEP_IDS.PRESCREENER_BILLING_PAYMENT,
              route: 'prescreener-billing-payment',
              title: 'booking.prescreener.categories.billingAndPayment',
              completedTitle: 'booking.prescreener.categories.billingAndPayment',
              isActive: false,
              condenseKey: CONDENSED_GROUPS.PRESCREENER_CATEGORIES,
              menuComponent: RequestStepsPrescreenerCategoryStep,
              forNewClientOnly: true,
            }),
            emberO.create({
              id: STEP_IDS.PRESCREENER_CLIENT_PREFERENCES,
              route: 'prescreener-client-preferences',
              title: 'booking.prescreener.categories.clientPreferences',
              completedTitle: 'booking.prescreener.categories.clientPreferences',
              isActive: false,
              condenseKey: CONDENSED_GROUPS.PRESCREENER_CATEGORIES,
              menuComponent: RequestStepsPrescreenerCategoryStep,
              forNewClientOnly: true,
            }),
          ]
        : []),
      ...(this.hasMessageStep
        ? [
            emberO.create({
              id: STEP_IDS.INTAKE_QUESTIONS,
              isActive: false,
              route: 'message',
              title: 'booking.steps.message',
              completedTitle: 'booking.steps.message',
              completedKey: 'hasCompletedMessageStep',
              menuComponent: RequestStepsMessageStep,
            }),
          ]
        : []),
      emberO.create({
        id: STEP_IDS.INTAKE_DOCUMENTS,
        isActive: false,
        route: 'channel-uploads',
        title: 'booking.steps.intakeDocuments',
        completedTitle: 'booking.steps.intakeDocuments',
        completedKey: 'channelUploadsComplete',
        menuComponent: RequestStepsChannelUploadsStep,
      }),
      emberO.create({
        id: STEP_IDS.EXTERNAL_DETAILS,
        isActive: false,
        route: 'channel-fields',
        title: 'booking.steps.externalDetails',
        completedTitle: 'booking.steps.externalDetails',
        completedKey: 'channelFields',
        menuComponent: RequestStepsChannelFieldsStep,
      }),
      emberO.create({
        id: STEP_IDS.CLIENT_INFORMATION,
        isActive: false,
        route: 'information.index',
        title: 'booking.steps.contactInformation',
        completedTitle: 'booking.steps.completedTitles.contactInformation',
        careCoordinatorTitle: 'booking.steps.completedTitles.memberInformation',
        careCoordinatorCompletedTitle: 'booking.steps.completedTitles.memberInformation',
        completedKey: '',
        menuComponent: RequestStepsInformationStep,
        forNewClientOnly: true,
      }),
      emberO.create({
        id: STEP_IDS.REVIEW_AND_CONFIRM,
        isActive: false,
        route: 'information.review',
        title: 'booking.steps.reviewConfirm',
        forNewClientOnly: true,
      }),
      emberO.create({
        id: STEP_IDS.REVIEW_AND_CONFIRM,
        isActive: false,
        route: 'confirmation',
        title: 'booking.steps.requestSent',
        previousStepDisabled: true,
      }),
      emberO.create({
        id: STEP_IDS.REQUEST_CANCELLED,
        isActive: false,
        route: 'cancelled',
        title: 'booking.steps.requestCancelled',
        previousStepDisabled: true,
      }),
      emberO.create({
        id: STEP_IDS.UNAVAILABLE,
        isActive: false,
        route: 'unavailable',
        title: 'booking.steps.unavailable',
        previousStepDisabled: true,
      }),
    ];
  }

  invalidateActiveStep(controller, routeName) {
    let activeSteps = controller.get('activeSteps');
    if (activeSteps) {
      activeSteps.forEach(step => {
        step.set('isActive', step.route === routeName);
      });
    }
  }

  findActiveSteps(reservation, controller) {
    let loadedClinicians = this.store.peekAll('clinician');
    let hasNonMonarchProfessions = loadedClinicians
      .slice()
      .some(({ hasMonarchProfession }) => !hasMonarchProfession);
    let hasSingleClinician = loadedClinicians.length === 1;
    let clinicianHasMonarchProfession = reservation.clinician?.hasMonarchProfession;

    this.set(
      'clinicianHasMonarchProfession',
      clinicianHasMonarchProfession || !hasNonMonarchProfessions
    );
    let activeSteps = [...this.steps];
    if (hasSingleClinician) {
      activeSteps = activeSteps.rejectBy('route', 'clinician');
    }

    activeSteps = this.#filterNewClientOnlyRoutes(activeSteps);

    activeSteps = this.#filterChannelCustomFieldsSteps(
      activeSteps,
      reservation.channel,
      controller
    );

    activeSteps = this.#filterPreselectedFields(activeSteps, controller, reservation);

    activeSteps = this.#mapForCondensedNav(activeSteps);

    activeSteps = this.prescreener.step.processPrescreenerSteps(reservation, activeSteps);

    activeSteps = this.#orderForReorderedNav(activeSteps);

    return activeSteps;
  }

  #filterNewClientOnlyRoutes(activeSteps) {
    if (this.session.currentClient) {
      return activeSteps.rejectBy('forNewClientOnly', true);
    }
    return activeSteps;
  }

  #filterChannelCustomFieldsSteps(activeSteps, channel, controller) {
    if (!channel?.hasUploads) {
      activeSteps = activeSteps.reject(step => step.route === 'channel-uploads');
    }
    if (!channel?.showStep(controller)) {
      activeSteps = activeSteps.reject(step => step.route === 'channel-fields');
    }
    if (channel && this.widgetToken.token) {
      activeSteps = activeSteps.reject(
        step => step.route === 'service' || step.route === 'message'
      );
    }

    return activeSteps;
  }

  #filterPreselectedFields(activeSteps, controller, reservation) {
    if (controller.searchByOffice && reservation.office) {
      activeSteps = activeSteps.rejectBy('route', 'location');
    }

    if (this.onlyOneClientTypeAllowedToBookAppt || controller.clientType) {
      activeSteps = activeSteps.rejectBy('route', 'client-type');
    }

    if (
      (controller.cptCodeId && reservation.cptCode) ||
      reservation.get('channel.widgetSelectChannelService')
    ) {
      activeSteps = activeSteps.rejectBy('route', 'service');
    }

    if (controller.selectedSlot && reservation.startTime) {
      activeSteps = activeSteps.rejectBy('route', 'date');
    }

    return activeSteps;
  }

  #isCondensed({ step }) {
    return !!step.condenseKey;
  }

  #decorateCondensed({ step }) {
    if (this.#isCondensed({ step })) {
      const condensedTitle = this.#getCondensedTitle(step.condenseKey);
      step.setProperties({
        condensedTitle,
        isCondensed: true,
      });
    }
  }

  #getCondensedTitle(condenseKey) {
    switch (condenseKey) {
      case CONDENSED_GROUPS.APPOINTMENT_INFO:
        return 'booking.steps.condensed.appointmentInfo';
      case CONDENSED_GROUPS.PRESCREENER_CATEGORIES:
        return 'booking.steps.condensed.prescreener';
      default:
        return 'booking.steps.condensed.yourInfo';
    }
  }

  #decorateContactInfo({ step }) {
    let title = this.featurePrescreenerV2
      ? 'booking.steps.contactInformation'
      : 'booking.steps.condensed.yourInfo';

    if (step.route === 'information.index') {
      step.set('condensedTitle', title);
    }
  }

  #mapForCondensedNav(activeSteps) {
    let numberLabel = this.isCondensed ? 0 : 1;
    let encountered = new Set();

    activeSteps.forEach((step, index) => {
      if (this.isCondensed) {
        let stepOptions = { step };
        let { condenseKey, id } = step;

        let encounteredKey = condenseKey ?? id;

        if (!encountered.has(encounteredKey)) {
          encountered.add(encounteredKey);
          numberLabel++;
        }

        this.#decorateCondensed(stepOptions);
        this.#decorateContactInfo(stepOptions);
        step.setProperties({
          canShowCondensedNav: true,
          numberLabel,
          index,
        });
      } else {
        step.setProperties({ index, numberLabel });
        numberLabel++;
      }
    });
    return activeSteps;
  }

  #orderForReorderedNav(activeSteps) {
    let { reorderedNavEnabled } = this.session;
    let additionalInfo = activeSteps.findBy('id', 'intake questions');
    let clientInfoStep = activeSteps.findBy('id', 'client information');
    let willReorder = reorderedNavEnabled && clientInfoStep;
    if (additionalInfo) {
      this.#decorateAdditionalInfo(additionalInfo);
      if (willReorder) {
        set(clientInfoStep, 'completedKey', 'hasCompletedInformationStep');
        activeSteps.removeObject(additionalInfo);
        activeSteps.insertAt(activeSteps.indexOf(clientInfoStep) + 1, additionalInfo);
      }
    }
    return activeSteps;
  }

  #decorateAdditionalInfo(additionalInfo) {
    let { isMdDown: isMobile } = this.media;
    let title = this.#getAdditionalInfoTitle();
    setProperties(additionalInfo, {
      title,
      completedTitle: title,
      mobileSuffix: isMobile ? 'booking.steps.condensed.optional' : '',
    });
    return additionalInfo;
  }

  #getAdditionalInfoTitle() {
    let { session, canShowPrescreenerNav } = this;
    let { reorderedNavEnabled, addReasonsForVisit } = session;
    if (reorderedNavEnabled) {
      return 'booking.steps.condensed.additionalInfo';
    }
    return addReasonsForVisit || canShowPrescreenerNav
      ? 'booking.steps.condensed.reasonForVisit'
      : 'booking.steps.message';
  }
}
